import { shallowRef, watch, computed } from 'vue';
import { defineStore } from 'pinia';
import { useAuthStore } from './auth.store';
import homeService from '@/shared/services/home.service';
import DateUtils from '@/shared/utilities/date-utils';
import { APP_ONBOARDING_STATUS } from '@/shared/constants/authentication';

// One patient-day check shared by Home, app resume and the mandatory root dialog.
export const useEmotionCheckinStore = defineStore('emotionCheckinStore', () => {
  const auth = useAuthStore();
  const choices = shallowRef([]);
  const open = shallowRef(false);
  const checkedDay = shallowRef('');
  const pending = shallowRef(null);
  const checking = computed(() => Boolean(pending.value));

  function check() {
    if (!auth.isLoggedIn || auth.appOnboardingStatus !== APP_ONBOARDING_STATUS.COMPLETED) {
      return Promise.resolve();
    }

    const day = DateUtils.getCurrentUserTimeZoneTime('YYYY-MM-DD');
    if (checkedDay.value === day) {
      return Promise.resolve();
    }
    pending.value ||= load(day);
    return pending.value;
  }

  async function load(day) {
    const version = auth.authStateVersion;
    try {
      const result = await homeService.getEmotionCheckinChoices();
      if (version !== auth.authStateVersion) {
        return;
      }
      choices.value = result.emotionCheckins;
      checkedDay.value = day;
      auth.account.isEmotionCheckinSubmittedToday = result.isSubmittedToday;
      open.value = choices.value.length > 0;
    } finally {
      if (version === auth.authStateVersion) {
        pending.value = null;
      }
    }
  }

  function submitted() {
    auth.account.isEmotionCheckinSubmittedToday = true;
  }

  watch(
    () => auth.authStateVersion,
    () => {
      choices.value = [];
      open.value = false;
      checkedDay.value = '';
      pending.value = null;
    },
    { flush: 'sync' },
  );

  return { choices, open, checkedDay, checking, check, submitted };
});
